import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, gramas } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/ds";
import { ImageUpload } from "@/components/ds/ImageUpload";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";

export const SETTING_FIELDS = [
  ["nozzle", "Bico (mm)", "0,4"],
  ["layer_height", "Altura de camada (mm)", "0,20"],
  ["infill", "Preenchimento (%)", "20"],
  ["infill_pattern", "Padrão de preenchimento", "Grade"],
  ["flow", "Vazão (%)", "100"],
  ["nozzle_temp", "Temperatura do bico (°C)", "215"],
  ["bed_temp", "Temperatura da mesa (°C)", "60"],
  ["speed", "Velocidade (mm/s)", "120"],
  ["walls", "Paredes / perímetros", "3"],
  ["supports", "Suportes", "Não"],
  ["brim", "Brim / saia", "Não"],
];

export function DevTestModal({ project, onClose, onSaved }) {
  const filaments = project.filaments || [];
  const plates = project.plates || [];
  const [plateIds, setPlateIds] = useState(plates.length === 1 ? [plates[0].id] : []);
  const [objective, setObjective] = useState("");
  const [items, setItems] = useState([]);
  const [quantity, setQuantity] = useState("1");
  const [settings, setSettings] = useState({});
  const [extraSettings, setExtraSettings] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const togglePlate = (id) =>
    setPlateIds((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/dev-projects/${project.id}/tests`, {
        objective,
        plate_ids: plateIds,
        filaments: items
          .filter((i) => i.filament_id)
          .map((i) => ({
            filament_id: i.filament_id,
            quantity_g: Number(i.quantity_g || 0) || 0.01,
            color_note: i.color_note,
          })),
        quantity: Number(quantity || 1),
        settings: { ...settings, outras: extraSettings },
        notes,
        photos: photo ? [photo] : [],
      });
      onSaved(data);
      toast.success("Teste planejado. Envie para a produção quando quiser imprimir.");
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const available = filaments.filter((f) => !items.some((i) => i.filament_id === f.id));

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Novo teste · tentativa #${(project.tests_count || 0) + 1}`}
      description="Aqui você planeja a tentativa. Nada é consumido do estoque até a produção acontecer."
      testid="dev-test-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <Field label="Objetivo do teste">
          <Input
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            placeholder="Ex.: testar encaixe da tampa"
            data-testid="dev-test-objective"
          />
        </Field>

        <div>
          <p className="mb-2 text-xs text-muted-foreground">Plates deste teste</p>
          {plates.length === 0 ? (
            <p className="rounded-lg border border-dashed border-white/15 bg-secondary/30 p-3 text-xs text-muted-foreground">
              Nenhuma Plate no projeto. Você pode criar o teste agora e vincular a Plate depois.
            </p>
          ) : (
            <div className="space-y-2">
              {plates.map((p) => {
                const on = plateIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePlate(p.id)}
                    data-testid={`dev-test-plate-${p.id}`}
                    className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                      on ? "border-brand/50 bg-brand/10" : "border-white/10 bg-secondary/40"
                    }`}
                  >
                    <span className="min-w-0 truncate">{p.name}</span>
                    <span className="shrink-0 font-num text-[11px] text-muted-foreground">
                      {p.output_quantity} un · {gramas(p.total_g)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <Field label="Quantidade a imprimir">
          <Input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="font-num"
            data-testid="dev-test-quantity"
          />
        </Field>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Filamentos e cores previstos (opcional)</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={available.length === 0}
              onClick={() =>
                setItems((list) => [
                  ...list,
                  { filament_id: available[0].id, quantity_g: "", color_note: "" },
                ])
              }
              data-testid="dev-test-add-filament-btn"
              className="h-8 gap-1.5 rounded-full text-xs"
            >
              <Plus className="h-3.5 w-3.5" /> Adicionar
            </Button>
          </div>
          <div className="space-y-2">
            {items.map((i, idx) => (
              <div
                key={idx}
                className="space-y-2 rounded-lg border border-white/10 bg-secondary/40 p-3"
                data-testid={`dev-test-item-${idx}`}
              >
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <NativeSelect
                      value={i.filament_id}
                      onChange={(v) =>
                        setItems((list) => list.map((x, k) => (k === idx ? { ...x, filament_id: v } : x)))
                      }
                      options={filaments.map((f) => ({
                        value: f.id,
                        label: `${f.material} ${f.color_name}`,
                      }))}
                      data-testid={`dev-test-item-filament-${idx}`}
                    />
                  </div>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={i.quantity_g}
                    onChange={(e) =>
                      setItems((list) =>
                        list.map((x, k) => (k === idx ? { ...x, quantity_g: e.target.value } : x))
                      )
                    }
                    placeholder="g prev."
                    className="h-10 w-24 font-num"
                    data-testid={`dev-test-item-qty-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setItems((list) => list.filter((_, k) => k !== idx))}
                    className="h-9 px-2 text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <Input
                  value={i.color_note}
                  onChange={(e) =>
                    setItems((list) =>
                      list.map((x, k) => (k === idx ? { ...x, color_note: e.target.value } : x))
                    )
                  }
                  placeholder="Onde essa cor entra (opcional)"
                  className="h-9 text-xs"
                  data-testid={`dev-test-item-note-${idx}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs text-muted-foreground">Configurações de impressão</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {SETTING_FIELDS.map(([key, label, ph]) => (
              <Field key={key} label={label}>
                <Input
                  value={settings[key] || ""}
                  onChange={(e) => setSettings((s) => ({ ...s, [key]: e.target.value }))}
                  placeholder={ph}
                  className="h-9"
                  data-testid={`dev-test-setting-${key}`}
                />
              </Field>
            ))}
          </div>
          <div className="mt-3">
            <Field label="Outras configurações">
              <Textarea
                value={extraSettings}
                onChange={(e) => setExtraSettings(e.target.value)}
                rows={2}
                placeholder="Qualquer outro ajuste usado nesta tentativa"
                data-testid="dev-test-setting-outras"
              />
            </Field>
          </div>
        </div>

        <Field label="Observações do planejamento">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            data-testid="dev-test-notes"
          />
        </Field>
        <Field label="Foto de referência (opcional)">
          <ImageUpload value={photo} onChange={setPhoto} testid="dev-test-photo" />
        </Field>

        <Button
          type="submit"
          disabled={busy}
          data-testid="save-dev-test-btn"
          className="h-12 rounded-full font-semibold transition-transform active:scale-95"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar teste
        </Button>
      </form>
    </ResponsiveModal>
  );
}
